// Stock-control strip shared by the outlet and portfolio financial dashboards.
//
// Values come from the gateway dashboard report (`stock` object):
//   stockValue  = QOH × weighted average cost (RM)
//   lowStock    = QOH > 0 and QOH <= reorder level
//   outOfStock  = QOH <= 0
//   slowMoving  = QOH > 0 and no sale in the last 90 days
//   negativeStock = QOH < 0 (data-integrity warning)

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { DashboardStockSummary, StockControlKind } from '../lib/api.js';
import { formatMoney } from '../lib/finance.js';

@customElement('stock-control-panel')
export class StockControlPanel extends LitElement {
  static styles = css`
    :host { display: block; }
    .panel {
      border: 1px solid var(--color-border);
      border-radius: 14px;
      background: var(--color-surface);
      padding: 0.9rem;
      margin-bottom: 0.85rem;
    }
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.4rem;
      margin-bottom: 0.6rem;
    }
    .head h3 { margin: 0; font-size: 0.9rem; }
    .head .hint { font-size: 0.7rem; color: var(--color-text-secondary); }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
      gap: 0.6rem;
    }
    .card {
      border: 1px solid var(--color-border);
      border-radius: 12px;
      padding: 0.65rem 0.75rem;
      min-width: 0;
      background: var(--color-surface);
      position: relative;
    }
    .card.clickable { cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
    .card.clickable:hover { border-color: var(--color-primary); box-shadow: 0 2px 8px rgba(15,35,80,0.08); }
    .card .chev, .alert .chev {
      position: absolute;
      right: 0.55rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--color-text-secondary);
      font-size: 1rem;
      font-weight: 800;
    }
    .alert.clickable { cursor: pointer; }
    .alert.clickable:hover { background: #fadbd8; }
    .card .label {
      display: block;
      font-size: 0.66rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--color-text-secondary);
    }
    .card strong {
      display: block;
      margin-top: 0.2rem;
      font-size: 1.2rem;
      font-weight: 800;
      overflow-wrap: anywhere;
    }
    .card small {
      display: block;
      margin-top: 0.15rem;
      font-size: 0.7rem;
      color: var(--color-text-secondary);
    }
    .card.warn { border-color: var(--color-warning, #e8a317); background: #fffaf0; }
    .card.warn strong { color: #8a5a00; }
    .card.risk { border-color: var(--color-error, #d93025); background: #fff5f4; }
    .card.risk strong { color: var(--color-error, #d93025); }
    .alert {
      position: relative;
      margin-top: 0.6rem;
      padding: 0.5rem 1.4rem 0.5rem 0.7rem;
      border-radius: 10px;
      background: #fdecea;
      color: #b71c1c;
      font-size: 0.76rem;
    }
  `;

  @property({ type: Object }) stock: DashboardStockSummary | null = null;
  /** When true, cards emit `stock-drilldown` instead of being static. */
  @property({ type: Boolean }) drilldown = false;

  private _open(kind: StockControlKind, label: string) {
    if (!this.drilldown) return;
    this.dispatchEvent(new CustomEvent('stock-drilldown', {
      detail: { kind, label },
      bubbles: true,
      composed: true,
    }));
  }

  private _card(
    kind: StockControlKind,
    label: string,
    value: unknown,
    hint: string,
    tone = '',
  ) {
    const clickable = this.drilldown;
    return html`
      <div
        class="card ${tone} ${clickable ? 'clickable' : ''}"
        data-testid="stock-${kind === 'value' ? 'value' : kind === 'low' ? 'low' : kind === 'out' ? 'out' : 'slow'}"
        role=${clickable ? 'button' : nothing}
        tabindex=${clickable ? '0' : nothing}
        title=${clickable ? `Show ${label.toLowerCase()} detail` : nothing}
        @click=${() => this._open(kind, label)}
        @keydown=${(e: KeyboardEvent) => {
          if (!clickable) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._open(kind, label);
          }
        }}
      >
        <span class="label">${label}</span>
        <strong>${value}</strong>
        <small>${hint}</small>
        ${clickable ? html`<span class="chev">›</span>` : nothing}
      </div>
    `;
  }

  render() {
    const s = this.stock;
    if (!s) return nothing;
    return html`
      <div class="panel" data-testid="stock-control">
        <div class="head">
          <h3>Stock control</h3>
          <span class="hint">
            QOH × weighted average cost${this.drilldown ? ' · click a card for detail' : ''}
          </span>
        </div>
        <div class="grid">
          ${this._card('value', 'Stock value', formatMoney(s.stockValue), `${s.skus} active SKU(s)`)}
          ${this._card('low', 'Low stock', s.lowStock, 'At/below reorder level', s.lowStock > 0 ? 'warn' : '')}
          ${this._card('out', 'Out of stock', s.outOfStock, 'QOH ≤ 0', s.outOfStock > 0 ? 'risk' : '')}
          ${this._card('slow', 'Slow movers', s.slowMoving, 'No sale in 90 days', s.slowMoving > 0 ? 'warn' : '')}
        </div>
        ${s.negativeStock > 0 ? html`
          <div
            class="alert ${this.drilldown ? 'clickable' : ''}"
            data-testid="stock-negative"
            role=${this.drilldown ? 'button' : nothing}
            tabindex=${this.drilldown ? '0' : nothing}
            @click=${() => this._open('negative', 'Negative stock')}
            @keydown=${(e: KeyboardEvent) => {
              if (!this.drilldown) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this._open('negative', 'Negative stock');
              }
            }}
          >
            ⚠ ${s.negativeStock} item(s) have negative QOH — investigate stock adjustments.
            ${this.drilldown ? html`<span class="chev">›</span>` : nothing}
          </div>
        ` : nothing}
      </div>
    `;
  }
}
